import { ResizeObserver } from '@juggle/resize-observer';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { FsPresenceDevice } from '@lib/api/entities';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth/services';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { MixpanelService } from '@lib/mixpanel';

import { IDeviceMarker } from '../../interfaces';
import { FsAxonService } from '../../services/axon.service';
import { FsAxonDeviceAdvancedMarker } from '../axon-device-marker/axon-device-marker';
import { AxonLivestreamIframeComponent } from '../axon-livestream-iframe/axon-livestream-iframe.component';

@Component({
  selector: 'fs-axon-device-popout',
  templateUrl: './axon-device-popout.component.html',
  styleUrls: ['./axon-device-popout.component.scss'],
})
export class AxonDevicePopoutComponent implements OnInit, AfterViewInit, OnDestroy {
  public dsaInit: boolean = false;
  public dsaAssignee: any;
  public displayedIcons: any[];
  public bringToFront = new Subject();

  private _header = 56;
  private _followingText = 28;
  private _sidePadding = 16;
  private _mousedownListener: (e: any) => void;
  private _mouseupListener: (e: any) => void;
  private _mouseleaveListener: (e: any) => void;

  private _destroy$ = new Subject<void>();

  @ViewChild(AxonLivestreamIframeComponent)
  public iframeComponent: AxonLivestreamIframeComponent;

  @HostListener('mousedown') componentClick() {
    this.bringToFront.next();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      marker: IDeviceMarker;
    },
    public _currentUser: SnCurrentUser,
    public _element: ElementRef,
    private readonly _sanitizer: DomSanitizer,
    private readonly _axonService: FsAxonService,
    private readonly _mixpanelService: MixpanelService,
    private readonly _matDialogRef: MatDialogRef<AxonDevicePopoutComponent>,
    private readonly _datadogService: FsDataDogRumService,
    private _currentOrganization: SnCurrentOrganization,
  ) {
    this._setupListeners();
  }

  get dialogContainer() {
    return (this._element.nativeElement as HTMLElement).parentNode.parentNode
      .parentNode as HTMLElement;
  }

  public ngOnInit(): void {
    if (this.data.marker instanceof FsAxonDeviceAdvancedMarker) {
      this.dsaInit = true;
      this.dsaAssignee = this.data.marker.device.attributes.assignees?.[0];
    } else {
      this.dsaInit = false;
    }
    this._filterIcons();
  }

  public ngAfterViewInit(): void {
    this._initListeners();
  }

  public ngOnDestroy(): void {
    const matDialogContainer: HTMLElement = this._element.nativeElement.parentElement;
    matDialogContainer.removeEventListener('mousedown', this._mousedownListener, true);
    matDialogContainer.removeEventListener('mouseup', this._mouseupListener, true);
    document.removeEventListener('mouseleave', this._mouseleaveListener, false);
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isUnitDetailsBetaEnabled(): boolean {
    return this._currentOrganization.settings?.unitDetailsBeta;
  }

  public setFollowMode(): void {
    const device = this.data.marker.device;
    const deviceInFollowMode = this._axonService.deviceInFollowMode;
    if (deviceInFollowMode) {
      this._axonService.leaveFollowMode(deviceInFollowMode);
    }
    this._axonService.enterFollowMode(device);
    this._axonService.iFrameFM(device);
    this.data.marker.setFollowMode(true);
  }

  public stopFollowMode(): void {
    const device = this.data.marker.device;
    if (device) {
      this._axonService.leaveFollowMode(device);
      this._axonService.iFrameFM(null);
      this.data.marker.setFollowMode(false);
    }
  }

  public close(): void {
    const device = this.data.marker.device;
    if (device) {
      this._matDialogRef.close();
      this._axonService.leaveFollowMode(device);
      this.data.marker.setFollowMode(false);

      this._mixpanelService.trackMetric(MxEventName.StreamStopCamAf, {
        [MxPropertyName.DeviceID]: device.axonDeviceId,
        [MxPropertyName.FususViewerOrganizationID]: this._currentOrganization.id,
        [MxPropertyName.FususCamOrgViewer]: this._currentOrganization.name,
        [MxPropertyName.FususStreamEndReason]: 'Close camera',
      });
    }
    const iframe: HTMLElement = this._element.nativeElement.querySelector('iframe');
    const matDialogContainer = this._element.nativeElement.parentElement;
    const spacer = this.data.marker.getFollowMode()
      ? this._header + this._followingText
      : this._header;
    iframe.style.height = `${matDialogContainer.getBoundingClientRect().height - spacer}px`;
  }

  public safeHTML(value: any): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(value);
  }

  public generateTooltip(content: any): string {
    return `${content.data.messageTranslation} - ${new FsFormatDatePipe().transform(
      content.data.notification.notificationReceivedTime,
      'HH:mm',
    )}hrs`;
  }

  public dockCamera(): void {
    const index = this._axonService.activePopouts.findIndex((popout) => {
      return popout.id === `fs__axon-device-popout-${this.data.marker.id}`;
    });
    if (index > -1) {
      this._axonService.activePopouts.splice(index, 1);
      this._matDialogRef.close();
    }
    this._axonService.dockBodyCamera(this.data.marker);

    if (this.data.marker.device) {
      this._axonService.addAnalytics('axon_popout_to_docked', {
        dockedCamera: this.data.marker.device.filteredAnaltyicData,
      });

      const trackingInfo = this._mixpanelService.createCameraTrackingInfo(
        { axonDevice: this.data.marker.device.filteredAnaltyicData },
        this._currentOrganization.id,
        this._currentOrganization.name,
        '',
        'Device Drawer - Dock Camera Button From Popout',
      );

      // Add device alert types if available (only for FsPresenceDevice)
      if (this.data.marker.device instanceof FsPresenceDevice) {
        const alertTypes = this.data.marker.device.alertTypes;
        if (alertTypes.length > 0) {
          trackingInfo[MxPropertyName.DeviceAlertTypes] = alertTypes;
        }
      }

      this._mixpanelService.trackMetric(MxEventName.StreamStartReqCamAf, trackingInfo);

      this._datadogService.addAction('livestream_attempt_start', {
        id: this.data.marker.device.axonDeviceId,
        source_ui: 'Device Drawer - Dock Camera Button From Popout',
      });
    }
  }

  private _initListeners(): void {
    const iframe: HTMLElement = this._element.nativeElement.querySelector('iframe');
    const matDialogContainer: HTMLElement = this._element.nativeElement.parentElement;
    const deviceContainer: HTMLElement =
      this._element.nativeElement.querySelector('.device-container');
    const observer = new ResizeObserver(() => {
      const spacer = this.data.marker.getFollowMode()
        ? this._header + this._followingText
        : this._header;
      iframe.style.height = `${matDialogContainer.getBoundingClientRect().height - spacer}px`;
    });
    setTimeout(() => {
      observer.observe(matDialogContainer);
      matDialogContainer.style.minWidth = `${
        matDialogContainer.getBoundingClientRect().width - this._sidePadding
      }px`;
      matDialogContainer.style.minHeight = `${
        matDialogContainer.getBoundingClientRect().height - this._header
      }px`;
    }, 500);
    this._mousedownListener = (e) => {
      if (e.target.id === this._matDialogRef.id) {
        deviceContainer.style.pointerEvents = 'none';
      }
    };
    this._mouseupListener = () => {
      deviceContainer.style.pointerEvents = 'all';
    };
    this._mouseleaveListener = () => {
      deviceContainer.style.pointerEvents = 'all';
    };
    matDialogContainer.addEventListener('mousedown', this._mousedownListener, true);
    matDialogContainer.addEventListener('mouseup', this._mouseupListener, true);
    document.addEventListener('mouseleave', this._mouseleaveListener, false);
  }

  private _filterIcons(): void {
    this.displayedIcons = [
      ...new Map(
        this.data.marker.bannerContent.map((content) => [content['icon'], content]),
      ).values(),
    ];
  }

  private _setupListeners(): void {
    this._axonService.iFrameFM$.pipe(takeUntil(this._destroy$)).subscribe((device) => {
      if (device) {
        const marker = this.data.marker;
        const isActive = device?.axonDeviceId === marker.device.axonDeviceId;

        if (device) {
          this._axonService.deviceInFollowMode = device;
        }
        marker.setFollowMode(isActive);
        // If active, stop follow mode in all other popouts
        if (isActive) {
          this._axonService.activePopouts.forEach(({ componentInstance }) => {
            if (componentInstance && componentInstance !== this) {
              componentInstance.stopFollowMode();
            }
          });
        }
      }
    });
  }
}
